import { createFileRoute } from '@tanstack/react-router';

import { YStack } from '@/components/ui/layout/layout';
import { useBrowserStreamQuery } from '@/features/browser-stream/browser-stream.query';
import { ChatSavedCredentialRequest } from '@/features/browser-stream/ChatSavedCredentialRequest';
import { LiveBrowserStreamCard } from '@/features/browser-stream/LiveBrowserStreamCard';
import {
  type SavedCredentialRequest as Request,
  useSavedCredentialRequestQuery,
} from '@/features/browser-stream/saved-credential.query';
import { useBrowserStreamInput } from '@/features/browser-stream/use-browser-stream-input';

const ActiveRequest = ({ request }: { request: Request }) => {
  const active =
    request.status === 'pending' ||
    (request.status === 'resolved' && !request.notified);
  const { iframeRef, canInteract, takeControl, returnControl } =
    useBrowserStreamInput(active);
  const stream = useBrowserStreamQuery(request.chat_id, active, false);

  return (
    <YStack className="relative h-[32rem] min-h-[20rem] w-full overflow-hidden rounded-lg border border-border bg-surface lg:h-[min(75vh,48rem)]">
      <LiveBrowserStreamCard
        iframeRef={iframeRef}
        canInteract={canInteract}
        streamUrl={stream.data?.stream_url}
        isLoading={stream.isLoading || stream.isFetching}
        isFullscreen={false}
        canToggleFullscreen={false}
        canClose={false}
        onToggleFullscreen={() => undefined}
        onClose={returnControl}
        onRefresh={() => void stream.refetch()}
        onTakeControl={takeControl}
        onReturnControl={returnControl}
      >
        <ChatSavedCredentialRequest
          request={request}
          onBeforeSubmit={returnControl}
        />
      </LiveBrowserStreamCard>
    </YStack>
  );
};

export const BrowserSavedCredential = () => {
  const { requestId } = Route.useParams();
  const { data, isPending, isError } =
    useSavedCredentialRequestQuery(requestId);

  return (
    <YStack className="mx-auto h-full w-full max-w-[90rem] gap-5 overflow-y-auto p-4 text-foreground sm:p-6">
      <h1 className="font-serif text-2xl">Browser saved credential</h1>
      {isPending ? <p role="status">Loading request…</p> : null}
      {isError ? <p role="alert">Could not load this request.</p> : null}
      {data ? <ActiveRequest key={data.request_id} request={data} /> : null}
    </YStack>
  );
};

export const Route = createFileRoute(
  '/_browser-request/browser-saved-credential/$requestId'
)({
  component: BrowserSavedCredential,
});
